import React, { useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ShieldCheck, IdCard, Wallet, TrendingUp, ClipboardList } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EmpleadosTab from "@/pages/rrhh/EmpleadosTab";
import NominasTab from "@/pages/rrhh/NominasTab";
import PrenominaTab from "@/pages/rrhh/PrenominaTab";
import LaboralPinGate from "@/pages/laboral/LaboralPinGate";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * LABORAL — grupo protegido con PIN personal (mismo que Cashflow Pro).
 * Sub-modules:
 *   - laboral_fichas       → EmpleadosTab
 *   - laboral_nominas      → NominasTab (informe completo del equipo)
 *   - laboral_evolucion    → NominasTab en tab evolución
 *   - laboral_prenomina    → PrenominaTab
 */

const TAB_META = {
  fichas: { label: "Fichas", icon: IdCard, testid: "laboral-tab-fichas" },
  nominas: { label: "Informe de nóminas", icon: Wallet, testid: "laboral-tab-nominas" },
  evolucion: { label: "Evolución salarial", icon: TrendingUp, testid: "laboral-tab-evolucion" },
  prenomina: { label: "Prenómina", icon: ClipboardList, testid: "laboral-tab-prenomina" },
};

// Mapeo submódulo del sidebar → tab por defecto.
const MODULE_TO_TAB = {
  laboral_fichas: "fichas",
  laboral_nominas: "nominas",
  laboral_evolucion: "evolucion",
  laboral_prenomina: "prenomina",
};

export default function LaboralPage({ moduleId }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Determina el tab activo: si viene por moduleId lo priorizamos, si viene por ?tab lo usamos.
  const initialTab = MODULE_TO_TAB[moduleId] || params.get("tab") || "fichas";
  const [tab, setTab] = useState(Object.keys(TAB_META).includes(initialTab) ? initialTab : "fichas");

  const accessQ = useQuery({
    queryKey: ["rrhh-access"],
    queryFn: async () => (await api.get("/rrhh/access")).data,
  });
  const canManage = !!accessQ.data?.can_manage;
  const canViewFichas = !!accessQ.data?.can_view_fichas;
  const canViewEvolucion = !!accessQ.data?.can_view_nominas_evolucion;
  const canPayroll = !!accessQ.data?.can_payroll;

  const onTabChange = (v) => {
    setTab(v);
    navigate(`/app/${moduleFor(v)}`);
  };

  return (
    <LaboralPinGate>
      <div className="space-y-5" data-testid="laboral-page">
        <div className="flex items-center gap-2.5 border-b pb-3">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <div>
            <h2 className="font-heading text-lg font-bold tracking-tight" data-testid="laboral-title">Laboral</h2>
            <p className="text-xs text-muted-foreground">Zona restringida · Dirección + Administración</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={onTabChange}>
          <TabsList className="border border-border/60 bg-background/60" data-testid="laboral-tabs">
            <TabsTrigger value="fichas" data-testid={TAB_META.fichas.testid}>
              <IdCard className="mr-1.5 h-4 w-4" />Fichas
            </TabsTrigger>
            <TabsTrigger value="nominas" data-testid={TAB_META.nominas.testid}>
              <Wallet className="mr-1.5 h-4 w-4" />Informe de nóminas
            </TabsTrigger>
            <TabsTrigger value="evolucion" data-testid={TAB_META.evolucion.testid}>
              <TrendingUp className="mr-1.5 h-4 w-4" />Evolución salarial
            </TabsTrigger>
            <TabsTrigger value="prenomina" data-testid={TAB_META.prenomina.testid}>
              <ClipboardList className="mr-1.5 h-4 w-4" />Prenómina
            </TabsTrigger>
          </TabsList>

          <TabsContent value="fichas" className="mt-4">
            <EmpleadosTab canViewEvolucion={canViewEvolucion} />
          </TabsContent>
          <TabsContent value="nominas" className="mt-4">
            {/* NominasTab: con canManage=true muestra vista completa del equipo (subir, filtro empleado, reprocesar). */}
            <NominasTab canManage={canViewFichas} canViewEvolucion={canViewEvolucion} />
          </TabsContent>
          <TabsContent value="evolucion" className="mt-4">
            {/* Reutiliza NominasTab; el sub-tab "Evolución" está dentro cuando canViewEvolucion=true. */}
            <NominasTab canManage={canViewFichas} canViewEvolucion={canViewEvolucion} />
          </TabsContent>
          <TabsContent value="prenomina" className="mt-4">
            {canPayroll ? <PrenominaTab /> : <SinPermisos label="Prenómina" />}
          </TabsContent>
        </Tabs>
      </div>
    </LaboralPinGate>
  );
}

function moduleFor(tab) {
  const map = {
    fichas: "laboral_fichas",
    nominas: "laboral_nominas",
    evolucion: "laboral_evolucion",
    prenomina: "laboral_prenomina",
  };
  return map[tab] || "laboral_fichas";
}

function SinPermisos({ label }) {
  return (
    <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
      No tienes permisos para acceder a <strong>{label}</strong>.
    </div>
  );
}
